// components/common/PriceHistoryChart.js
// Graphique SVG pur — évolution du prix/m² avec données réelles + fallback simulé
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../../config';
import './PriceHistoryChart.css';

// ─── Dimensions SVG ──────────────────────────────────────────────────────────
const W = 580, H = 210;
const PL = 68, PR = 20, PT = 22, PB = 36;
const PW = W - PL - PR;
const PH = H - PT - PB;

// ─── Données simulées (fallback si API insuffisante) ─────────────────────────
function genSimulated(commune, prixM2Actuel, mois) {
  const seed = (commune || 'x').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const now  = new Date();
  const data = [];
  let val = prixM2Actuel * (0.87 + (seed % 21) * 0.01);
  for (let i = mois - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleDateString('fr-FR', { month: 'short', year: i === 0 || i === mois - 1 ? '2-digit' : undefined });
    const raw = ((seed * (i * 11 + 17)) % 67) - 30;
    if (i > 0) val = val * (1 + raw / 1000);
    else val = prixM2Actuel;
    // Simuler une plage min/max autour de la moyenne
    const spread = val * (0.08 + (seed % 5) * 0.02);
    data.push({
      label, avg: Math.round(val),
      min: Math.round(val - spread),
      max: Math.round(val + spread),
      nb: 3 + (seed % 8), simulated: true,
    });
  }
  return data;
}

// ─── Courbe Bézier lissée ────────────────────────────────────────────────────
function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1], curr = pts[i];
    const cpx  = (prev.x + curr.x) / 2;
    d += ` C ${cpx},${prev.y} ${cpx},${curr.y} ${curr.x},${curr.y}`;
  }
  return d;
}

// ─── Aire Bézier lissée ──────────────────────────────────────────────────────
function smoothArea(pts, baseY) {
  if (pts.length < 2) return '';
  const line = smoothPath(pts);
  return `${line} L ${pts[pts.length - 1].x},${baseY} L ${pts[0].x},${baseY} Z`;
}

// ─── Bande min/max (area entre min et max) ───────────────────────────────────
function bandPath(ptsMin, ptsMax) {
  if (!ptsMin.length) return '';
  const top    = smoothPath(ptsMax);
  const bottom = [...ptsMin].reverse().map((p, i) =>
    i === 0 ? `L ${p.x},${p.y}` : `C ${(ptsMin[ptsMin.length - 1 - i].x + p.x) / 2},${ptsMin[ptsMin.length - i].y} ${(ptsMin[ptsMin.length - 1 - i].x + p.x) / 2},${p.y} ${p.x},${p.y}`
  ).join(' ');
  return `${top} ${bottom} Z`;
}

// ─── Projection linéaire 3 mois ──────────────────────────────────────────────
function projectPoints(pts, toX, toY, n = 3) {
  if (pts.length < 3) return [];
  const last = pts.slice(-3);
  const slope = (last[2].avg - last[0].avg) / 2;
  return Array.from({ length: n }, (_, i) => ({
    x: toX(pts.length + i),
    y: toY(Math.max(0, last[2].avg + slope * (i + 1))),
    val: Math.max(0, Math.round(last[2].avg + slope * (i + 1))),
    projected: true,
  }));
}

// ─── Formater les grands nombres ─────────────────────────────────────────────
function fmtK(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${Math.round(v / 1000)}k`;
  return String(v);
}

// ═════════════════════════════════════════════════════════════════════════════
export default function PriceHistoryChart({ commune, quartier, prixM2 }) {
  const loc = commune || quartier || '';

  const [periode, setPeriode]   = useState(12);   // 3 | 6 | 12 | 24
  const [data, setData]         = useState([]);
  const [reference, setRef]     = useState({ national: null, wilaya: null, wilayaNom: null });
  const [source, setSource]     = useState('loading'); // 'real' | 'simulated' | 'loading'
  const [hovered, setHovered]   = useState(null);
  const [showBand, setShowBand] = useState(true);
  const [showProj, setShowProj] = useState(true);
  const [showRef,  setShowRef]  = useState(true);
  const svgRef = useRef(null);

  // ── Fetch données réelles ────────────────────────────────────────────────
  useEffect(() => {
    if (!loc || !prixM2) return;
    setSource('loading');
    setHovered(null);

    fetch(`${API_URL}/tools/prix-historique?commune=${encodeURIComponent(loc)}&mois=${periode}`)
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (json && json.donnees && json.donnees.length >= 3) {
          setData(json.donnees);
          setRef(json.reference || {});
          setSource('real');
        } else {
          // Fallback : données simulées
          setData(genSimulated(loc, prixM2, periode));
          setRef({ national: prixM2 * 0.92, wilaya: prixM2 * 0.97, wilayaNom: null });
          setSource('simulated');
        }
      })
      .catch(() => {
        setData(genSimulated(loc, prixM2, periode));
        setRef({ national: prixM2 * 0.92, wilaya: prixM2 * 0.97, wilayaNom: null });
        setSource('simulated');
      });
  }, [loc, prixM2, periode]);

  // ── Export PNG ────────────────────────────────────────────────────────────
  const exportPNG = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const xml    = new XMLSerializer().serializeToString(svg);
    const blob   = new Blob([xml], { type: 'image/svg+xml' });
    const url    = URL.createObjectURL(blob);
    const img    = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = svg.viewBox.baseVal.width  * 2;
      canvas.height = svg.viewBox.baseVal.height * 2;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FAF9F5';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const a  = document.createElement('a');
      a.download = `prix-m2-${loc}-${periode}mois.png`;
      a.href     = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = url;
  }, [loc, periode]);

  if (!prixM2 || prixM2 <= 0 || !loc) return null;
  if (source === 'loading' || data.length === 0) {
    return (
      <div className="phc-root">
        <div className="phc-loading">
          <div className="phc-spinner" />
          Chargement de l'historique…
        </div>
      </div>
    );
  }

  // ── Calculs géométrie ────────────────────────────────────────────────────
  // +3 points pour la projection
  const totalPts   = data.length + 3;
  const xStep      = PW / (totalPts - 1);
  const toX        = i  => PL + i * xStep;

  const avgVals    = data.map(d => d.avg);
  const minVals    = data.map(d => d.min || d.avg * 0.92);
  const maxVals    = data.map(d => d.max || d.avg * 1.08);

  const allVals    = [...avgVals, ...minVals, ...maxVals];
  if (reference.national) allVals.push(reference.national);
  if (reference.wilaya)   allVals.push(reference.wilaya);

  const rawMin = Math.min(...allVals);
  const rawMax = Math.max(...allVals);
  const range  = rawMax - rawMin || prixM2 * 0.15;
  const vMin   = rawMin - range * 0.15;
  const vMax   = rawMax + range * 0.15;
  const vRange = vMax - vMin;

  const toY = v => PT + PH - ((v - vMin) / vRange) * PH;

  const pts    = data.map((d, i) => ({ x: toX(i), y: toY(d.avg), avg: d.avg, ...d }));
  const ptsMin = data.map((d, i) => ({ x: toX(i), y: toY(d.min || d.avg * 0.92) }));
  const ptsMax = data.map((d, i) => ({ x: toX(i), y: toY(d.max || d.avg * 1.08) }));
  const proj   = showProj ? projectPoints(data, toX, toY) : [];

  // Pic et creux
  const idxMax = avgVals.indexOf(Math.max(...avgVals));
  const idxMin = avgVals.indexOf(Math.min(...avgVals));

  // Tendance
  const trend    = data[data.length - 1].avg - data[0].avg;
  const trendPct = ((trend / data[0].avg) * 100).toFixed(1);
  const up       = trend >= 0;
  const color    = up ? '#BA7517' : '#1D9E75';
  const gradId   = `phcGrad-${loc.replace(/\s/g, '')}`;
  const bandId   = `phcBand-${loc.replace(/\s/g, '')}`;
  const projId   = `phcProj-${loc.replace(/\s/g, '')}`;

  // Grille Y (5 ticks)
  const yTicks = Array.from({ length: 5 }, (_, i) => ({
    y: PT + PH * (i / 4),
    v: Math.round(vMax - vRange * (i / 4)),
  }));

  // Chemins Bézier
  const linePath  = smoothPath(pts);
  const areaPath  = smoothArea(pts, PT + PH);
  const bandShape = showBand ? bandPath(ptsMin, ptsMax) : '';
  const projPath  = proj.length >= 2
    ? `M ${pts[pts.length - 1].x},${pts[pts.length - 1].y} ` + smoothPath([pts[pts.length - 1], ...proj]).slice(pts[pts.length - 1].x.toString().length + 2)
    : '';

  // Ligne de référence nationale
  const refYNat = reference.national ? toY(reference.national) : null;
  const refYWil = reference.wilaya   ? toY(reference.wilaya)   : null;

  return (
    <div className="phc-root">
      {/* ── En-tête ── */}
      <div className="phc-header">
        <div className="phc-title-group">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <span className="phc-title">Évolution prix/m² — {loc}</span>
          {source === 'real'
            ? <span className="phc-badge-real">données réelles</span>
            : <span className="phc-badge-sim">estimé</span>
          }
        </div>
        <div className="phc-header-right">
          <span className={`phc-trend ${up ? 'up' : 'down'}`}>
            {up ? '↑' : '↓'} {Math.abs(trendPct)}%
            <span className="phc-period"> / {periode} mois</span>
          </span>
        </div>
      </div>

      {/* ── Contrôles ── */}
      <div className="phc-controls">
        <div className="phc-periods">
          {[3, 6, 12, 24].map(m => (
            <button
              key={m}
              className={`phc-period-btn${periode === m ? ' active' : ''}`}
              onClick={() => setPeriode(m)}
            >
              {m === 24 ? '2 ans' : `${m} mois`}
            </button>
          ))}
        </div>
        <div className="phc-toggles">
          <label className="phc-toggle-label">
            <input type="checkbox" checked={showBand} onChange={e => setShowBand(e.target.checked)} />
            <span>Plage</span>
          </label>
          <label className="phc-toggle-label">
            <input type="checkbox" checked={showProj} onChange={e => setShowProj(e.target.checked)} />
            <span>Projection</span>
          </label>
          <label className="phc-toggle-label">
            <input type="checkbox" checked={showRef} onChange={e => setShowRef(e.target.checked)} />
            <span>Référence</span>
          </label>
          <button className="phc-export-btn" onClick={exportPNG} title="Télécharger en PNG">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            PNG
          </button>
        </div>
      </div>

      {/* ── Graphique SVG ── */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="phc-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Dégradé aire principale */}
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
          {/* Dégradé bande min/max */}
          <linearGradient id={bandId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.10" />
            <stop offset="100%" stopColor={color} stopOpacity="0.03" />
          </linearGradient>
          {/* Dégradé projection */}
          <linearGradient id={projId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor={color} stopOpacity="0.25" />
          </linearGradient>
          <clipPath id="phcClip">
            <rect x={PL} y={PT - 4} width={PW} height={PH + 4} />
          </clipPath>
        </defs>

        {/* ── Grille ── */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={PL} y1={tick.y} x2={W - PR} y2={tick.y}
              stroke="#e8e0d6"
              strokeWidth={i === 4 ? 1.5 : 0.7}
              strokeDasharray={i === 4 ? 'none' : '5,4'}
            />
            <text x={PL - 7} y={tick.y + 4} textAnchor="end" fontSize="8.5" fill="#a09080">
              {fmtK(tick.v)}
            </text>
          </g>
        ))}

        {/* ── Lignes de référence ── */}
        {showRef && refYNat && (
          <g>
            <line x1={PL} y1={refYNat} x2={W - PR} y2={refYNat}
              stroke="#9c9a92" strokeWidth="1" strokeDasharray="6,3" opacity="0.6" />
            <text x={W - PR + 3} y={refYNat + 4} fontSize="7.5" fill="#9c9a92">
              {reference.wilayaNom ? `Moy. ${reference.wilayaNom}` : 'Moy. nat.'}
            </text>
          </g>
        )}
        {showRef && refYWil && refYWil !== refYNat && (
          <g>
            <line x1={PL} y1={refYWil} x2={W - PR} y2={refYWil}
              stroke="#85563A" strokeWidth="1" strokeDasharray="3,4" opacity="0.5" />
          </g>
        )}

        {/* ── Bande min/max ── */}
        {showBand && bandShape && (
          <path d={bandShape} fill={`url(#${bandId})`}
            stroke={color} strokeWidth="0.5" strokeOpacity="0.2"
            clipPath="url(#phcClip)" />
        )}

        {/* ── Aire principale ── */}
        <path d={areaPath} fill={`url(#${gradId})`} clipPath="url(#phcClip)" />

        {/* ── Courbe principale Bézier ── */}
        <path
          d={linePath}
          fill="none" stroke={color} strokeWidth="2.4"
          strokeLinejoin="round" strokeLinecap="round"
          clipPath="url(#phcClip)"
        />

        {/* ── Projection ── */}
        {showProj && proj.length > 0 && (
          <>
            <path
              d={`M ${pts[pts.length - 1].x},${pts[pts.length - 1].y} ${proj.map(p => `L ${p.x},${p.y}`).join(' ')}`}
              fill="none" stroke={`url(#${projId})`}
              strokeWidth="2" strokeDasharray="5,4" strokeLinecap="round"
              clipPath="url(#phcClip)"
            />
            {/* Étiquette fin de projection */}
            <text
              x={proj[proj.length - 1].x}
              y={proj[proj.length - 1].y - 9}
              textAnchor="middle" fontSize="8" fill={color} fontStyle="italic"
            >
              {fmtK(proj[proj.length - 1].val)}
            </text>
          </>
        )}

        {/* ── Annotations pic / creux ── */}
        {idxMax !== idxMin && (
          <>
            {/* Pic ▲ */}
            <g>
              <text x={pts[idxMax].x} y={pts[idxMax].y - 11}
                textAnchor="middle" fontSize="8" fill="#ba6017" fontWeight="700">
                ▲ {fmtK(data[idxMax].avg)}
              </text>
            </g>
            {/* Creux ▼ */}
            <g>
              <text x={pts[idxMin].x} y={pts[idxMin].y + 16}
                textAnchor="middle" fontSize="8" fill="#1D9E75" fontWeight="700">
                ▼ {fmtK(data[idxMin].avg)}
              </text>
            </g>
          </>
        )}

        {/* ── Points interactifs + étiquettes X ── */}
        {pts.map((p, i) => {
          const showX  = i === 0 || i === pts.length - 1 || (pts.length <= 12 ? i % 2 === 1 : i % 4 === 0);
          const isHov  = hovered === i;
          const isPic  = i === idxMax;
          const isCreux = i === idxMin;
          const tipRight = p.x < W / 2;
          const tipX   = tipRight ? p.x + 8 : p.x - 90;
          return (
            <g key={i}>
              {showX && (
                <text x={p.x} y={H - 4} textAnchor="middle" fontSize="8" fill="#a09080">
                  {p.label}
                </text>
              )}
              {/* Zone hover invisible plus large */}
              <rect
                x={p.x - 12} y={PT} width={24} height={PH}
                fill="transparent"
                style={{ cursor: 'crosshair' }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              />
              <circle
                cx={p.x} cy={p.y}
                r={isHov || isPic || isCreux ? 5 : 3}
                fill={isPic ? '#ba6017' : isCreux ? '#1D9E75' : color}
                stroke="white" strokeWidth="2"
                style={{ pointerEvents: 'none' }}
              />

              {/* Tooltip hover */}
              {isHov && (
                <g>
                  <line x1={p.x} y1={p.y + 5} x2={p.x} y2={PT + PH}
                    stroke={color} strokeWidth="1" strokeDasharray="3,2" opacity="0.4" />
                  <rect x={tipX} y={p.y - 38} width={86} height={data[i]?.nb ? 38 : 26} rx={6}
                    fill="#2c2218" opacity="0.93" />
                  <text x={tipX + 43} y={p.y - 24} textAnchor="middle" fontSize="10.5" fill="white" fontWeight="700">
                    {p.avg.toLocaleString('fr-DZ')} DA/m²
                  </text>
                  {data[i]?.nb > 0 && (
                    <text x={tipX + 43} y={p.y - 10} textAnchor="middle" fontSize="8" fill="rgba(255,255,255,0.6)">
                      {data[i].nb} annonce{data[i].nb > 1 ? 's' : ''}
                    </text>
                  )}
                </g>
              )}
            </g>
          );
        })}

        {/* ── Points projection ── */}
        {showProj && proj.map((p, i) => (
          <circle key={`proj-${i}`}
            cx={p.x} cy={p.y} r={3}
            fill="white" stroke={color} strokeWidth="1.8" strokeDasharray="none"
            opacity={0.6 - i * 0.15}
          />
        ))}
      </svg>

      {/* ── Légende ── */}
      <div className="phc-legend">
        <div className="phc-legend-item">
          <span className="phc-legend-line" style={{ background: color }} />
          Prix/m² moyen {loc}
        </div>
        {showBand && (
          <div className="phc-legend-item">
            <span className="phc-legend-band" style={{ background: color }} />
            Plage min–max
          </div>
        )}
        {showRef && reference.national && (
          <div className="phc-legend-item">
            <span className="phc-legend-dashed" />
            {reference.wilayaNom ? `Moy. ${reference.wilayaNom}` : 'Moyenne nationale'}
          </div>
        )}
        {showProj && (
          <div className="phc-legend-item phc-legend-proj">
            <span className="phc-legend-proj-line" style={{ borderColor: color }} />
            Projection 3 mois
          </div>
        )}
      </div>

      <p className="phc-disclaimer">
        {source === 'real'
          ? `* Basé sur ${data.reduce((s, d) => s + (d.nb || 0), 0)} annonces réelles Darna Immo.`
          : '* Données estimées — à titre indicatif uniquement (Darna Immo).'}
      </p>
    </div>
  );
}
